import {ChangeDetectorRef, Component, EventEmitter, Input, Output, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {AbstractControl, ReactiveFormsModule, UntypedFormBuilder, ValidationErrors, Validators} from '@angular/forms';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Constants} from '../../constants';
import {UserModel} from '../../models';
import {AlertService, AuthService, SubscribersService} from '../../services';
import {PebblConfirmComponent} from '../../../shared/components/pebbl-confirm/pebbl-confirm.component';
import {FormAutofocusDirective} from '../../../shared/directives/form-autofocus.directive';

/**
 * "Edit Profile" dialog opened from the header profile menu. Self-hosted
 * overlay + panel (not NgbModal) so the panel chrome matches the Pebbl
 * profile-dialog design; the form/API logic is the one that used to live
 * inline in the header.
 */
@Component({
  hostDirectives: [FormAutofocusDirective],
  selector: 'app-edit-profile',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './edit-profile.component.html',
  styleUrl: './edit-profile.component.css'
})
export class EditProfileComponent {

  /** Current user — owned by the header, used to prefill the form. */
  @Input() identity: UserModel | undefined;
  /** Resolved profile-photo URL (already sized) or null → show the fallback. */
  @Input() avatarUrl: string | null = null;
  /** Inline silhouette used when there is no photo / the photo 404s. */
  @Input() fallbackImage = '';

  /** signal, not a plain field — open() is invoked from the header's
   *  template, and under zoneless CD a plain write would never re-render
   *  this component (the click only marks the header dirty) */
  isOpen = signal(false);

  userForm: any;
  /** true while the save request is in flight (footer shows "Saving…") */
  submitted = false;
  /** flips to true on an invalid submit so the field errors become visible */
  isLoading = false;
  /** the picked File, uploaded after the profile PATCH succeeds */
  files: any;
  /** local data-URL preview of the picked file */
  preview: SafeUrl | undefined;
  /** set when the stored photo 404s, so the avatar falls back to the initials */
  photoFailed = false;
  /** true while the remove-image request is in flight */
  removing = false;

  /** Removal is published so the header can drop its own copy of the photo. */
  @Output() photoRemoved = new EventEmitter<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private authenticationService: AuthService,
    private subscriberService: SubscribersService,
    private alertService: AlertService,
    private sanitizer: DomSanitizer,
    private changeDetectorRef: ChangeDetectorRef,
    private ngbModal: NgbModal,
  ) {
    this.userForm = this.fb.group({
      // required + no-whitespace: a name of only spaces must NOT pass. The custom
      // validator reports `required` for a blank/whitespace-only value so the
      // existing "Enter first/last name." messages cover both cases.
      first_name: ['', [Validators.required, EditProfileComponent.nonBlankValidator]],
      last_name: ['', [Validators.required, EditProfileComponent.nonBlankValidator]],
      email: ['', [Validators.required, Validators.email]],
    });
  }

  /** Treat a whitespace-only value as empty (Validators.required alone accepts "   "). */
  static nonBlankValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value != null && String(value).trim().length === 0) {
      return {required: true};
    }
    return null;
  }

  get uf() {
    return this.userForm.controls;
  }

  /** True while a stored photo is on screen — gates the remove control. */
  get hasPhoto(): boolean {
    return !this.preview && !!this.avatarUrl && !this.photoFailed;
  }

  get fullName(): string {
    return `${this.identity?.first_name || ''} ${this.identity?.last_name || ''}`.trim();
  }

  /**
   * First + last initial, matching the account/contact identity headers — the default
   * shown once a photo is removed.
   */
  get initials(): string {
    const first = (this.identity?.first_name || '').trim();
    const last = (this.identity?.last_name || '').trim();
    const value = (first[0] || '') + (last[0] || '');
    return value ? value.toUpperCase() : '-';
  }

  open(): void {
    this.isLoading = false;
    this.submitted = false;
    this.files = undefined;
    this.preview = undefined;
    this.photoFailed = false;
    this.removing = false;
    this.userForm.reset();
    if (this.identity) {
      this.userForm.patchValue({
        first_name: this.identity.first_name,
        last_name: this.identity.last_name,
        email: this.identity.email
      });
    }
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
    this.files = undefined;
    this.preview = undefined;
    this.isLoading = false;
    this.submitted = false;
  }

  handleFileInput(event: any) {
    const reader = new FileReader();
    if (event.target.files && event.target.files.length) {
      if (event.target.files[0].type.includes('image')) {
        if (event.target.files[0].size < 25000000) {
          this.files = <File>event.target.files[0];
          reader.readAsDataURL(this.files);
          reader.onload = () => {
            // Sanitize the data URL so the <img> src binding accepts it.
            this.preview = this.sanitizer.bypassSecurityTrustUrl(reader.result as string);
            this.changeDetectorRef.markForCheck();
          };
        } else {
          this.alertService.info(Constants.USER_UPDATE_PROFILE_CONSTANTS.IMAGE_SIZE_VALIDATION);
        }
      } else {
        this.alertService.info(Constants.USER_UPDATE_PROFILE_CONSTANTS.IMAGE_TYPE_VALIDATION_MESSAGE);
      }
      // let the same file be re-picked after a rejected upload
      event.target.value = '';
    }
  }

  onSubmit() {
    // stop here if form is invalid
    if (this.userForm.invalid) {
      this.isLoading = true;
      return;
    } else {
      this.isLoading = false;
    }
    this.submitted = true;
    let userDataForm: any = {
      // trim so surrounding whitespace is never persisted (the validator already
      // blocks whitespace-only names)
      first_name: (this.uf.first_name.value || '').trim(),
      last_name: (this.uf.last_name.value || '').trim(),
      email: this.uf.email.value,
    };
    this.authenticationService.updateProfile(userDataForm).subscribe((userModel: UserModel) => {
      if (this.files !== undefined) {
        const frmData = new FormData();
        frmData.append('profile_picture', this.files);
        this.authenticationService.updateProfilePicture(frmData).subscribe((data: any) => {
          this.submitted = false;
          if (data.profile_picture !== null) {
            // S3_USER_LOGO_IMAGE_KIT_URL already ends in "/" — adding another
            // one yields /profile-picture//file.jpg, which 404s to the silhouette.
            userModel.profile_picture = Constants.S3_USER_LOGO_IMAGE_KIT_URL + data.profile_picture;
          }
          this.applyProfile(userModel);
        }, () => {
          this.submitted = false;
          this.files = undefined;
          this.preview = undefined;
          this.changeDetectorRef.markForCheck();
        });
      } else {
        this.submitted = false;
        if (userModel.profile_picture !== null) {
          userModel.profile_picture = Constants.S3_USER_LOGO_IMAGE_KIT_URL + userModel.profile_picture;
        }
        this.applyProfile(userModel);
      }
    }, (error: any) => {
      this.submitted = false;
      if (error.status == 422) {
        let self = this;
        if (error.error.data.length > 0) {
          Object.keys(error.error.data).forEach(function (key, data) {
            self.userForm.controls[error.error.data[key].field].setErrors({'apiError': error.error.data[key].message});
          });
        }
      }
      this.changeDetectorRef.markForCheck();
    });
  }

  /**
   * Remove the profile image: confirm, delete, then fall back to the initials avatar
   * with the upload control still in place so a new photo can be picked straight away.
   *
   * The dialog stays open — removing the image is not the same as saving the form, and
   * the user may still be editing their name/email.
   */
  removePhoto(): void {
    const ref = this.ngbModal.open(PebblConfirmComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'pebbl-modal-window pebbl-confirm-window'
    });
    ref.componentInstance.title = 'Remove profile image?';
    ref.componentInstance.message =
      'Are you sure you want to remove this profile image? Your initials will be shown until a new one is uploaded.';
    ref.componentInstance.confirmLabel = 'Remove Image';
    ref.componentInstance.danger = true;

    ref.result.then((ok: boolean) => {
      if (!ok) {
        return;
      }
      this.removing = true;
      this.changeDetectorRef.markForCheck();
      this.authenticationService.deleteProfilePicture().subscribe(() => {
        this.removing = false;
        // Clearing avatarUrl locally is what swaps in the initials: the @Input is not
        // re-pushed until the header re-renders, and photoRemoved is what makes it do so.
        this.avatarUrl = null;
        this.photoFailed = false;
        if (this.identity) {
          this.identity.profile_picture = null as any;
        }
        this.photoRemoved.emit();
        this.alertService.success(Constants.SUCCESS_MESSAGE_OF_PROFILE_IMAGE_REMOVED, 'Success');
        // Nothing ticks change detection after the confirm-modal promise chain resolves.
        this.changeDetectorRef.detectChanges();
      }, () => {
        this.removing = false;
        this.changeDetectorRef.detectChanges();
      });
    }, () => { /* dismissed — nothing to do */ });
  }

  /** Publish the saved user so the header avatar/name refresh, then close. */
  private applyProfile(userModel: UserModel): void {
    this.authenticationService.setAdminDetail(userModel);
    this.subscriberService._profileModel.next(userModel);
    this.alertService.success(Constants.SUCCESS_USER_PROFILE_UPDATE, 'Success');
    this.close();
    this.changeDetectorRef.markForCheck();
  }
}
